import { BP_SCALE, type BasisPoints, dec, type Kopecks, type Minutes } from '@glory/kernel';

import { t } from './translate.ts';

const MINUS = '−';
const NBSP = ' ';

/** Целые рубли — без копеек, с копейками — всегда две цифры: «327,60 ₽», а не «327,6 ₽». */
const moneyFormats = [0, 2].map(
  (digits) =>
    new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }),
);
const rublesFormats = [0, 2].map(
  (digits) =>
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }),
);
const pick = <T>(formats: readonly T[], value: Kopecks): T =>
  (value % 100 === 0 ? formats[0] : formats[1]) as T;
const numberFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 20 });
const hoursFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2, useGrouping: false });
const percentFormat = new Intl.NumberFormat('ru-RU', { style: 'percent', maximumFractionDigits: 2 });
const multiplierFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4, useGrouping: false });

// Русская типографика: знак минус вместо дефиса.
const minus = (s: string) => s.replace('-', MINUS);

const rubles = (value: Kopecks): number => dec(value).div(100).toNumber();

/** 12 260 000 коп. → «122 600 ₽»; копейки показываются, только если они есть. */
export function formatMoney(value: Kopecks, options: { perHour?: boolean } = {}): string {
  const text = minus(pick(moneyFormats, value).format(rubles(value)));
  return options.perHour ? t('units.perHour', { value: text }) : text;
}

/** Сумма без знака валюты: «57 644». */
export function formatRubles(value: Kopecks): string {
  return minus(pick(rublesFormats, value).format(rubles(value)));
}

export function formatNumber(value: number): string {
  return minus(numberFormat.format(value));
}

/** Минуты → часы десятичной дробью: 90 → «1,5». */
export function formatHours(value: Minutes): string {
  return hoursFormat.format(dec(value).div(60).toDecimalPlaces(2).toNumber());
}

/** Минуты → «1 ч 30 мин». */
export function formatDuration(value: Minutes): string {
  const h = Math.floor(value / 60);
  const m = value % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(t('units.hoursShort', { value: h }));
  if (m > 0 || h === 0) parts.push(t('units.minutesShort', { value: m }));
  return parts.join(' ').replaceAll(' ', NBSP);
}

/** Базисные пункты → «47,02 %». */
export function formatPercent(value: BasisPoints): string {
  return minus(percentFormat.format(dec(value).div(BP_SCALE).toNumber()));
}

/** Множитель в базисных пунктах → «×1,3». */
export function formatMultiplier(value: BasisPoints): string {
  return `×${multiplierFormat.format(dec(value).div(BP_SCALE).toNumber())}`;
}

const bytesFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });
const BYTE_UNITS = ['units.bytes', 'units.kilobytes', 'units.megabytes', 'units.gigabytes'] as const;

/** Размер файла: 1536 → «1,5 КБ», 52 428 800 → «50 МБ» (двоичные единицы, как в проводнике). */
export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? value : Math.round(value * 10) / 10;
  return t(BYTE_UNITS[unit] ?? 'units.bytes', { value: bytesFormat.format(rounded) });
}

export const DEFAULT_TIME_ZONE = 'Europe/Moscow';

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});
const dateShortFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  timeZone: 'UTC',
});

/** Дата без времени (тип `date`, строка YYYY-MM-DD) → «28.09.2026». */
export function formatDate(value: string): string {
  return dateFormat.format(new Date(`${value}T00:00:00Z`));
}

export function formatDateShort(value: string): string {
  return dateShortFormat.format(new Date(`${value}T00:00:00Z`));
}

const dayLongFormat = new Intl.DateTimeFormat('ru-RU', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const dayMonthFormat = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' });
const weekdayFormat = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', timeZone: 'UTC' });
const monthFormat = new Intl.DateTimeFormat('ru-RU', { month: 'long', timeZone: 'UTC' });

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Дата с днём недели — заголовок дня: «Пятница, 9 января 2026 г.». */
export function formatDayLong(value: string): string {
  return capitalize(dayLongFormat.format(new Date(`${value}T00:00:00Z`)));
}

/** День и месяц словом: «9 января». */
export function formatDayMonth(value: string): string {
  return dayMonthFormat.format(new Date(`${value}T00:00:00Z`));
}

/** День недели коротко: «пт». */
export function formatWeekday(value: string): string {
  return weekdayFormat.format(new Date(`${value}T00:00:00Z`));
}

/** Название месяца (`month` — 1…12): «Январь». */
export function formatMonth(month: number): string {
  return capitalize(monthFormat.format(new Date(Date.UTC(2000, month - 1, 1))));
}

const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

/** Момент времени (UTC) → «28.09.2026, 14:30» в часовом поясе студии. */
export function formatDateTime(value: string | Date, timeZone: string = DEFAULT_TIME_ZONE): string {
  let format = dateTimeFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
    dateTimeFormats.set(timeZone, format);
  }
  return format.format(typeof value === 'string' ? new Date(value) : value);
}

const MINUTE_MS = 60_000;
const agoDayFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  timeZone: DEFAULT_TIME_ZONE,
});
const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone: DEFAULT_TIME_ZONE });

/**
 * Коротко «когда»: «сейчас», «5 мин», «3 ч» (в пределах суток), «вчера», иначе «26.09».
 * Дни считаются по часовому поясу студии.
 */
export function formatAgo(value: Date, now: Date = new Date()): string {
  const diff = Math.max(0, now.getTime() - value.getTime());
  if (diff < MINUTE_MS) return t('units.justNow');
  if (diff < 60 * MINUTE_MS) return t('units.minutesAgo', { value: Math.floor(diff / MINUTE_MS) });
  const today = dayKey.format(now);
  if (dayKey.format(value) === today || diff < 6 * 60 * MINUTE_MS) {
    return t('units.hoursAgo', { value: Math.floor(diff / (60 * MINUTE_MS)) });
  }
  const yesterday = dayKey.format(new Date(now.getTime() - 24 * 60 * MINUTE_MS));
  if (dayKey.format(value) === yesterday) return t('units.yesterday');
  return agoDayFormat.format(value);
}
